import { BaseSocialLoginButton } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/BaseSocialLoginButton";
import {
  defaultSSOErrorMessage,
  extractMessage,
} from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/errorMessages";
import {
  GoogleResponse,
  GoogleSuccessResponse,
  GoogleErrorResponse,
} from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/types";
import {
  ISocialSignOnButtonProps,
  useHandleSSOSuccess,
} from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/useHandleSSOSuccess";
import { useScriptCheck } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/useScriptCheck";
import {
  isGoogleLoginSciptLoaded,
  loadGoogleLoginScript,
} from "@faire/retailer-visitor-shared/initializers/loadGoogleLoginScript";
import { getGoogleSSOClientID } from "@faire/retailer-visitor-shared/serialized-data/getGoogleSSOClientID";
import { useSSOErrorStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import getIDToken from "@faire/web-api--source/endpoints/www/api/v2/users/google/oauth-token/post";
import { SocialSignOnProvider } from "@faire/web-api--source/indigofair/data/SocialSignOnProvider";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web/common/localization";
import { logError } from "@faire/web/common/logging";
import React, { FC, useCallback, useEffect } from "react";

const PROVIDER = SocialSignOnProvider.GOOGLE;
const SCOPE = "openid profile email";

export const GoogleLoginButtonV2: FC<ISocialSignOnButtonProps> = (props) => {
  const { disabled, isOnSignIn, sourceId } = props;
  const { strictLocalize } = useStrictLocalization();
  const { isScriptLoading, hasScriptError } = useScriptCheck({
    isScriptLoaded: isGoogleLoginSciptLoaded,
    loadScript: loadGoogleLoginScript,
    provider: PROVIDER,
  });
  const handleSSOSuccess = useHandleSSOSuccess({
    ...props,
    provider: PROVIDER,
  });

  const codeClientRef = React.useRef<
    google.accounts.oauth2.CodeClient | undefined
  >(undefined);

  const googleSSOClientID = getGoogleSSOClientID();
  const { errorMessage, setErrorMessage } = useSSOErrorStore();
  const { userClickedSSOButton, recordSSOButtonClick } = useSSOStore();

  const onSuccess = useCallback(
    async ({ code }: GoogleSuccessResponse) => {
      const { sso_token } = await getIDToken({ authorization_code: code });
      const idToken = sso_token?.id_token;
      if (idToken) {
        handleSSOSuccess(idToken);
      } else {
        setErrorMessage(defaultSSOErrorMessage(strictLocalize));
      }
    },
    [handleSSOSuccess, setErrorMessage, strictLocalize]
  );

  const onFailure = useCallback(
    (errorResponse: GoogleErrorResponse) => {
      const { error } = errorResponse;
      logError(errorResponse, {
        tags: {
          signInType: "SSO",
          provider: PROVIDER,
          code: error,
        },
      });
      if (error) {
        const errorMessage = extractMessage(
          {
            provider: PROVIDER,
            code: error,
          },
          strictLocalize
        );
        setErrorMessage(errorMessage);
      }
    },
    [setErrorMessage, strictLocalize]
  );

  const handleResponse = useCallback(
    (response: GoogleResponse) =>
      response.error ? onFailure(response) : onSuccess(response),
    [onFailure, onSuccess]
  );

  // Initialise
  useEffect(() => {
    if (!isScriptLoading) {
      try {
        const windowObj = getWindowOrThrow();
        if (
          windowObj?.google?.accounts?.oauth2 &&
          typeof windowObj?.google.accounts.oauth2.initCodeClient === "function"
        ) {
          codeClientRef.current = google.accounts.oauth2.initCodeClient({
            client_id: googleSSOClientID ?? "",
            scope: SCOPE,
            callback: (response: unknown) =>
              handleResponse(response as GoogleResponse),
          });
        }
      } catch (error) {
        logError(error, {
          tags: {
            signInType: "SSO",
            provider: PROVIDER,
            code: "auth_init_failed",
          },
        });
        setErrorMessage(defaultSSOErrorMessage(strictLocalize));
      }
    }
  }, [
    isScriptLoading,
    googleSSOClientID,
    setErrorMessage,
    handleResponse,
    codeClientRef,
    strictLocalize,
  ]);

  if (hasScriptError) {
    return (
      <BaseSocialLoginButton
        onClick={() =>
          setErrorMessage(
            strictLocalize({
              id: "JQATH5",
              defaultMessage:
                "Google script is blocked. Unblock and refresh to use Google sign in.",
              description: {
                text: "Error message for unable to use Google log in",
              },
            })
          )
        }
        socialProvider={PROVIDER}
        disabled={disabled}
        sourceId={sourceId}
      />
    );
  }

  if (!userClickedSSOButton && errorMessage) {
    return (
      <BaseSocialLoginButton
        onClick={recordSSOButtonClick}
        socialProvider={PROVIDER}
        disabled={disabled}
        isOnSignIn={isOnSignIn}
        sourceId={sourceId}
      />
    );
  }

  return (
    <BaseSocialLoginButton
      disabled={disabled}
      onClick={() => codeClientRef.current?.requestCode()}
      socialProvider={PROVIDER}
      loading={isScriptLoading}
      isOnSignIn={isOnSignIn}
      sourceId={sourceId}
    />
  );
};
